import { useSearchParams } from 'react-router';
import { useCallback, useState, useEffect, useRef } from 'react';
import { Icon } from './ui/Icon.js';
import { Select } from './Select.js';
import { DateInput } from './DateInput.js';

const ENGINES = [
  { value: 'chatgpt', label: 'ChatGPT' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'claude', label: 'Claude' },
  { value: 'google_aio', label: 'Google AIO' },
  { value: 'mistral', label: 'Mistral' },
];

const SENTIMENTS = [
  { value: '', label: 'All' },
  { value: 'positive', label: 'Positive' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'negative', label: 'Negative' },
];

const BRAND_MENTIONED = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const CATEGORIES = [
  { value: '', label: 'All categories' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'comparison', label: 'Comparison' },
  { value: 'evaluation', label: 'Evaluation' },
  { value: 'consideration', label: 'Consideration' },
  { value: 'problem_solving', label: 'Problem solving' },
  { value: 'recommendation', label: 'Recommendation' },
];

// Sort options (Spec 150). Each value encodes `sort:order`; the BE supports
// sort ∈ {captured_at, latency_ms} and order ∈ {asc, desc}. Newest-first is the
// default and is treated as the unset state.
const SORT_DEFAULT = 'captured_at:desc';
const SORT_OPTIONS = [
  { value: 'captured_at:desc', label: 'Newest first' },
  { value: 'captured_at:asc', label: 'Oldest first' },
  { value: 'latency_ms:desc', label: 'Slowest first' },
  { value: 'latency_ms:asc', label: 'Fastest first' },
];


interface ResponseFiltersProps {
  onFiltersChange: () => void;
}

export function ResponseFilters({ onFiltersChange }: ResponseFiltersProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedEngines = (searchParams.get('engine') ?? '').split(',').filter(Boolean);
  const dateFrom = searchParams.get('date_from') ?? '';
  const dateTo = searchParams.get('date_to') ?? '';
  const brandMentioned = searchParams.get('brand_mentioned') ?? '';
  const sentimentLabel = searchParams.get('sentiment_label') ?? '';
  const domainCited = searchParams.get('domain_cited') ?? '';
  const promptCategory = searchParams.get('prompt_category') ?? '';
  const searchParam = searchParams.get('search') ?? '';

  // Sort: two URL params (`sort` + `order`) back a single control (Spec 150).
  // Empty/unset is treated as the default (Newest first).
  const sortParam = searchParams.get('sort') ?? '';
  const orderParam = searchParams.get('order') ?? '';
  const sortValue =
    sortParam && orderParam ? `${sortParam}:${orderParam}` : SORT_DEFAULT;

  const updateParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        // Reset to page 1 when filters change
        next.delete('offset');
        return next;
      });
      onFiltersChange();
    },
    [setSearchParams, onFiltersChange],
  );

  // Sort handler (Spec 150): one control sets both `sort` + `order`. Selecting
  // the default clears both params so the URL stays clean.
  const handleSortChange = useCallback(
    (value: string) => {
      const [sort, order] = value.split(':');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value && value !== SORT_DEFAULT && sort && order) {
          next.set('sort', sort);
          next.set('order', order);
        } else {
          next.delete('sort');
          next.delete('order');
        }
        next.delete('offset');
        return next;
      });
      onFiltersChange();
    },
    [setSearchParams, onFiltersChange],
  );

  // Debounced search (Spec 048)
  const [searchInput, setSearchInput] = useState(searchParam);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setSearchInput(searchParam);
  }, [searchParam]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateParam('search', value);
    }, 300);
  }, [updateParam]);

  // Spec 249 (review P1-14): one engine select, like Domains', replacing five
  // pills that were a filter toggle — which a Kridt chip never is. The API
  // still takes a list, so a shared link carrying several engines keeps
  // filtering; the select then says so rather than showing one of them.
  const engineValue = selectedEngines.join(',');
  const engineOptions = [
    { value: '', label: 'All engines' },
    ...ENGINES,
    ...(selectedEngines.length > 1 ? [{ value: engineValue, label: `${selectedEngines.length} engines` }] : []),
  ];

  const clearFilters = useCallback(() => {
    setSearchParams({});
    onFiltersChange();
  }, [setSearchParams, onFiltersChange]);

  const hasActiveFilters =
    selectedEngines.length > 0 ||
    dateFrom ||
    dateTo ||
    brandMentioned ||
    sentimentLabel ||
    domainCited ||
    promptCategory ||
    searchParam;

  // Spec 249 (review P1-14): the table archetype's toolbar, directly above
  // the results — it was eight controls stacked in a bordered plate in three
  // field styles. Search first, then labelled selects, one date range, and
  // Sort at the end. Every field has a visible label.
  return (
    <div className="hub-toolbar" data-testid="response-filters" role="search" aria-label="Filter responses">
      <div className="hub-field">
        <label className="hub-field__label" htmlFor="responses-search">Search</label>
        <span className="hub-input-wrap">
          <Icon name="bx-search" />
          <input
            id="responses-search"
            type="search"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Words in the response"
            className="hub-input"
            data-testid="search-input"
          />
        </span>
      </div>

      <div className="hub-field">
        <label className="hub-field__label" htmlFor="responses-engine">Engine</label>
        <Select
          id="responses-engine"
          options={engineOptions}
          value={engineValue}
          onChange={(v) => updateParam('engine', v)}
          placeholder="All engines"
          className="w-40"
          data-testid="engine-filter"
        />
      </div>

      <div className="hub-field">
        <label className="hub-field__label" htmlFor="responses-brand-mentioned">Brand mentioned</label>
        <Select
          id="responses-brand-mentioned"
          options={BRAND_MENTIONED}
          value={brandMentioned}
          onChange={(v) => updateParam('brand_mentioned', v)}
          placeholder="All"
          className="w-32"
          data-testid="brand-mentioned-filter"
        />
      </div>

      <div className="hub-field">
        <label className="hub-field__label" htmlFor="responses-sentiment">Sentiment</label>
        <Select
          id="responses-sentiment"
          options={SENTIMENTS}
          value={sentimentLabel}
          onChange={(v) => updateParam('sentiment_label', v)}
          placeholder="All"
          className="w-32"
          data-testid="sentiment-filter"
        />
      </div>

      <div className="hub-field">
        <label className="hub-field__label" htmlFor="responses-category">Prompt category</label>
        <Select
          id="responses-category"
          options={CATEGORIES}
          value={promptCategory}
          onChange={(v) => updateParam('prompt_category', v)}
          placeholder="All categories"
          className="w-44"
          data-testid="prompt-category-filter"
        />
      </div>

      <div className="hub-field">
        <label className="hub-field__label" htmlFor="responses-domain-cited">Domain cited</label>
        <input
          id="responses-domain-cited"
          type="text"
          value={domainCited}
          onChange={(e) => updateParam('domain_cited', e.target.value)}
          placeholder="e.g. runnersworld.com"
          className="hub-input w-48"
          data-testid="domain-cited-filter"
        />
      </div>

      {/* One date-range control: a single label over the pair, each end named
          for a screen reader. */}
      <div className="hub-field" role="group" aria-labelledby="responses-dates-label">
        <span className="hub-field__label" id="responses-dates-label">Captured</span>
        <span className="flex items-center gap-2">
          <DateInput
            value={dateFrom}
            onChange={(v) => updateParam('date_from', v)}
            aria-label="From"
            data-testid="date-from"
          />
          <span className="text-sm text-[var(--hub-color-ink-muted)]">to</span>
          <DateInput
            value={dateTo}
            onChange={(v) => updateParam('date_to', v)}
            aria-label="To"
            data-testid="date-to"
          />
        </span>
      </div>

      <div className="hub-field">
        <label className="hub-field__label" htmlFor="responses-sort">Sort</label>
        <Select
          id="responses-sort"
          options={SORT_OPTIONS}
          value={sortValue}
          onChange={handleSortChange}
          placeholder="Newest first"
          className="w-40"
          data-testid="sort-filter"
        />
      </div>

      {hasActiveFilters && (
        <button type="button" onClick={clearFilters} className="hub-btn hub-btn--quiet" data-testid="clear-filters">
          <Icon name="bx-x" />
          Clear filters
        </button>
      )}
    </div>
  );
}
